"use client";

import { SanityImage } from "@/components/atoms/SanityImage";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Heading } from "@/components/atoms/Heading";
import type { HeroProps } from "./Hero.props";

interface HeroCaseStudyContentProps {
  data: HeroProps["data"];
}

/**
 * Client component for HeroCaseStudy's animated inner content.
 * Kept separate so HeroCaseStudy can remain a server component (import "server-only").
 */
export const HeroCaseStudyContent = ({ data }: HeroCaseStudyContentProps) => {
  return (
    <Box className="flex flex-col md:flex-row items-center gap-12 lg:gap-24">
      {/* Text column */}
      <Box className="w-full md:w-1/2 text-left">
        <Box className="mb-6 aq-reveal-up aq-delay-1">
          <Text className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
            Our Work /{" "}
            <Text className="text-foreground">{data.title}</Text>
          </Text>
        </Box>

        {data.title && (
          <Heading level={1} className="text-4xl md:text-5xl lg:text-7xl font-extrabold tracking-tight text-foreground mb-6 leading-display-xs aq-reveal-up aq-delay-2">
            {data.title}
            {data.titleLine2 && (
              <Text className="block text-primary">
                {` ${data.titleLine2}`}
              </Text>
            )}
          </Heading>
        )}

        {data.subtitle && (
          <Text as="p" className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-medium max-w-2xl aq-reveal-up aq-delay-3">
            {data.subtitle}
          </Text>
        )}
      </Box>

      {/* Image */}
      {data.image && (
        <Box className="w-full md:w-1/2 aq-reveal-up aq-delay-2">
          <Box className="relative rounded-2xl overflow-hidden shadow-2xl aspect-photo">
            <SanityImage
              image={data.image}
              fill
              priority
              aspectRatio="4/3"
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover w-full h-full"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
